import {
  authoringModes,
  integrationOptionsSchema,
  type IntegrationOptions,
  type AuthoringMode,
} from "@proof/sdk";
import { defaultModuleTheme, type ModuleTheme } from "@proof/core";
import { Button, Control } from "./components";

export const authoringModeLabels: Record<AuthoringMode, string> = {
  designer: "Document designer",
  "reusable-forms": "Reusable form builder",
  configuration: "Field configuration",
  outputs: "Output profiles",
  "global-profiles": "Global profiles",
  "validation-presets": "Validation presets",
  "formatting-presets": "Formatting presets",
  summary: "Project summary",
};
export function IntegrationControls({
  options,
  onChange,
  authoring,
  onRemember,
}: {
  options: ReturnType<typeof integrationOptionsSchema.parse>;
  onChange(options: IntegrationOptions): void;
  authoring: boolean;
  onRemember(): void;
}) {
  const theme = { ...defaultModuleTheme, ...options.theme };
  const changeTheme = (patch: ModuleTheme) =>
    onChange({ ...options, theme: { ...options.theme, ...patch } });
  return (
    <section className="panel padded" aria-label="Integration controls">
      <h2>Appearance & permissions</h2>
      <p>Configure this embed, preview the result, then copy its setup code.</p>
      <label>
        <input
          type="checkbox"
          checked={options.designMode}
          onChange={(event) =>
            onChange({ ...options, designMode: event.target.checked })
          }
        />
        Design mode
      </label>
      {(
        [
          ["allowProjectRename", "Allow project name editing"],
          ["showHeading", "Show editor heading"],
          ["showDescription", "Show editor description"],
        ] as const
      ).map(([key, label]) => (
        <label key={key}>
          <input
            type="checkbox"
            checked={options.editor[key]}
            onChange={(event) =>
              onChange({
                ...options,
                editor: { ...options.editor, [key]: event.target.checked },
              })
            }
          />
          {label}
        </label>
      ))}
      {authoring && (
        <fieldset>
          <legend>Editor features</legend>
          {(
            [
              ["enableStatements", "Statements"],
              ["enableRules", "Rules and calculated values"],
              ["enableFormatting", "Formatting"],
              ["enableValidation", "Validation"],
              ["enableDynamicSources", "Dynamic data sources"],
              ["enableConditionalVisibility", "Conditional visibility"],
              ["enablePartOverrides", "Part overrides"],
            ] as const
          ).map(([key, label]) => (
            <label key={key}>
              <input
                type="checkbox"
                checked={options.editor[key] !== false}
                onChange={(event) =>
                  onChange({
                    ...options,
                    editor: { ...options.editor, [key]: event.target.checked },
                  })
                }
              />
              {label}
            </label>
          ))}
          <p>
            Controls affect editor presentation. Existing settings and server
            permissions are preserved. Open the editor's v6 compatibility list
            to review features that require the extended-data adapter.
          </p>
        </fieldset>
      )}
      <div className="control-grid">
        <Control label="Embed theme">
          <select
            value={theme.mode}
            onChange={(e) =>
              changeTheme({ mode: e.target.value as ModuleTheme["mode"] })
            }
          >
            <option value="system">Follow device</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </Control>
        <Control label="Embed brand color">
          <input
            type="color"
            value={theme.primary}
            onChange={(e) => changeTheme({ primary: e.target.value })}
          />
        </Control>
        <Control label="Embed accent color">
          <input
            type="color"
            value={theme.accent}
            onChange={(e) => changeTheme({ accent: e.target.value })}
          />
        </Control>
        <Control label="Embed font">
          <select
            value={theme.font}
            onChange={(e) =>
              changeTheme({ font: e.target.value as ModuleTheme["font"] })
            }
          >
            {[
              "Inter",
              "System",
              "Georgia",
              "Libre Baskerville",
              "Monospace",
            ].map((font) => (
              <option key={font}>{font}</option>
            ))}
          </select>
        </Control>
        <Control label="Embed density">
          <select
            value={theme.density}
            onChange={(e) =>
              changeTheme({ density: e.target.value as ModuleTheme["density"] })
            }
          >
            <option value="compact">Compact</option>
            <option value="comfortable">Comfortable</option>
            <option value="spacious">Spacious</option>
          </select>
        </Control>
        <Control label="Embed corner radius">
          <input
            type="number"
            min={0}
            max={24}
            value={theme.radius}
            onChange={(e) => {
              const radius = Number(e.target.value);
              if (Number.isFinite(radius) && radius >= 0 && radius <= 24)
                changeTheme({ radius });
            }}
          />
        </Control>
      </div>
      <fieldset>
        <legend>Allowed actions</legend>
        {(
          [
            ["edit", "Allow editing"],
            ["proof", "Allow proof preview"],
            ...(authoring ? [["export", "Allow project export"]] : []),
          ] as ["edit" | "proof" | "export", string][]
        ).map(([key, label]) => (
          <label className="embed-preview-toggle" key={key}>
            <input
              type="checkbox"
              checked={options.permissions[key]}
              onChange={(e) =>
                onChange({
                  ...options,
                  permissions: {
                    ...options.permissions,
                    [key]: e.target.checked,
                  },
                })
              }
            />{" "}
            {label}
          </label>
        ))}
      </fieldset>
      {authoring && (
        <fieldset>
          <legend>Enabled editor flows</legend>
          <div className="control-grid">
            {authoringModes.map((mode) => (
              <label className="embed-preview-toggle" key={mode}>
                <input
                  type="checkbox"
                  checked={options.allowedModes.includes(mode)}
                  onChange={(e) =>
                    onChange({
                      ...options,
                      allowedModes: e.target.checked
                        ? [...options.allowedModes, mode]
                        : options.allowedModes.filter((item) => item !== mode),
                    })
                  }
                />{" "}
                Enable {authoringModeLabels[mode].toLowerCase()}
              </label>
            ))}
          </div>
          {!options.allowedModes.length && (
            <p role="status">All editor flows are disabled.</p>
          )}
        </fieldset>
      )}
      <p className="muted">
        With editing disabled, authoring shows a read-only summary and forms
        display disabled controls. Your host must authorize document access and
        saving with server permissions.
      </p>
      <Button onClick={onRemember}>Remember settings in this browser</Button>
      <p className="muted">
        Remembered settings apply to this project’s embed examples in this
        browser. Copy the setup code to apply them in your host application.
      </p>
    </section>
  );
}
